import {
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';
import { toast } from 'react-toastify';

import getSites from '../../../api/private/users_management/gets/getAllFacias';
import postAddGroup from '../../../api/private/users_management/posts/postAddGroup';

function NewGroupModal({
  onAddSuccess,
  open,
  handleClose,
}) {
  const [siteNames, setSiteNames] = React.useState([]);
  const [combinedSites, setCombinedSites] = React.useState([]);
  const [groupName, setGroupName] = React.useState('');
  const [siteIds, setSiteIds] = React.useState([]);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const isValidGroupName = (groupname) => /^[A-Za-z\s]*$/.test(groupname);

  const disableCreateButton = () => {
    const emptygroupName = groupName?.length === 0;
    const emptysiteNames = siteNames?.length === 0;
    return emptygroupName || emptysiteNames;
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    // 👇 This ensures the dropdown stays inside the Dialog
    disablePortal: true,
  };

  const addGroup = async () => {
    try {
      await postAddGroup(groupName, siteIds, 1);
      handleClose(false);
      onAddSuccess(true);
      toast.success('Successfully created group');
    } catch (err) {
      console.error(err);
    }
  };

  const handleSiteChange = (event) => {
    const {
      target: { value },
    } = event;
    if (value.includes('All')) {
      setSiteNames(
        siteNames.length === combinedSites.length ? [] : [...combinedSites.map((item) => item.siteName)],
      );
      setSiteIds(siteNames.length === combinedSites.length ? [] : [...combinedSites.map((site) => site.siteId)]);
    } else {
      setSiteNames(typeof value === 'string' ? value.split(',') : value);
      const selectedValues = value.map((name) => combinedSites.find((item) => item.siteName === name)?.siteId);
      setSiteIds(selectedValues);
    }
  };

  const combineSites = (data) => {
    const sites = [];
    if (Array.isArray(data)) {
      data.forEach((obj) => {
        obj.Sites.forEach((site) => {
          sites.push({
            ...site,
            parent: obj.Name,
            SiteGroupId: obj.SiteGroupId,
          });
        });
      });
      return sites;
    }
    return [];
  };

  React.useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const sites = await getSites();
        if (sites.data.length > 0) {
          const combined = combineSites(sites.data);
          setCombinedSites(combined.map((item) => ({
            siteId: item.SiteId,
            siteName: item.Name,
          })));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchInitialData();
  }, []);

  const handleDelete = (value) => {
    setSiteNames((prevSelected) => prevSelected.filter((item) => item !== value));
  };

  const selectedAll = siteNames.length === combinedSites.length;
  const isValid = isValidGroupName(groupName);

  const boxStyle = {
    transition: '',
    padding: combinedSites.length > 0 ? '16px' : '8px',
    border: '1px solid #90caf9',
  };

  return (
    <Container maxWidth="sm">
      <Dialog
        open={open}
        onClose={() => handleClose(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreen}
      >
        <DialogTitle component="h5" sx={{ textAlign: 'center', fontSize: '2rem' }}>
          Add New Group
        </DialogTitle>
        <DialogContent>

          <Stack spacing={3}>
            <FormControl fullWidth>
              <Typography variant="subtitle1" gutterBottom>
                Group Name
              </Typography>
              <TextField
                size="small"
                type="text"
                label="Group Name"
                value={groupName}
                error={!isValid}
                onChange={(e) => setGroupName(e.target.value)}
              />
              {!isValid && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ mt: 1 }}
                >
                  Group name must contain only letters and spaces.
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle1" gutterBottom>
                Site Access
              </Typography>
              <Select
                disabled={groupName?.length === 0 || !isValid}
                multiple
                value={siteNames}
                onChange={handleSiteChange}
                input={<OutlinedInput label="Sites" />}
                renderValue={(selected) => (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                      gap: '5px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      paddingRight: '8px',
                    }}
                    className="custom-scrollbar"
                  >
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        onDelete={() => handleDelete(value)}
                        size="small"
                        sx={{
                          backgroundColor: '#4682B4',
                          color: 'white',
                          fontWeight: 400,
                          '& .MuiChip-deleteIcon': { color: 'white' },
                          borderRadius: '20px',
                        }}
                      />
                    ))}
                  </div>
                )}
                MenuProps={MenuProps}
                sx={boxStyle}
              >
                <MenuItem value="All">
                  <Checkbox checked={selectedAll} />
                  <ListItemText primary="All" />
                </MenuItem>
                {combinedSites.map((site) => (
                  <MenuItem key={site.siteId} value={site.siteName}>
                    <Checkbox checked={siteNames.includes(site.siteName)} />
                    <ListItemText primary={site.siteName} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => handleClose(false)} color="secondary">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={addGroup}
            disabled={disableCreateButton()}
          >
            CREATE
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default NewGroupModal;
